import * as React from 'react'
import { PageHeader, Well } from '@pushly/aqe/lib/components/index'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { convertToStringDay } from '@halo/_utils/relative-language-builder.util'
import { Breadcrumb, Col, Divider, Popover, Row, Tag } from 'antd'
import { LoadableDataState } from '@halo/portals/post-auth/domains/feeds/feed-page'
import aqe from '@pushly/aqe'
import { FeedDto } from '@halo/dtos/feed.dto'
import { IFixedFeedSchedule } from '@pushly/aqe/lib/interfaces/index'
import { secondsToHours, secondsToMinutes } from '@pushly/aqe/lib/utils/index'
import { FeedSendStrategy, RelativeDateDisplayMetric } from '@pushly/aqe/lib/enums/index'
import moment from 'moment-timezone'
import { DOMAINS_ROUTE } from '@halo/portals/post-auth/domains'
import { useService } from '@pushly/aqe/lib/hooks/index'
import { AppService } from '@halo/services/index'
import classNames from 'classnames'
import FormLayoutRow from '@halo/components/form-layout-row/form-layout-row'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { SegmentDto } from '@halo/dtos/segment.dto'
import SkeletonBlock from '@halo/components/skeleton-block/skeleton-block'

interface IFPHProps {
    domainName: string
    feed: LoadableDataState<FeedDto>
}

export const FeedPageHeader = (props: IFPHProps) => {
    const appService = useService(AppService)
    const { data, loading } = props.feed

    const handleBreadcrumbClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (!ev.metaKey) {
            ev.preventDefault()
            appService.route(ev.currentTarget.getAttribute('href'))
        }
    }

    function buildGeneralDetailsWell(feed: FeedDto) {
        const { enabled, url } = feed

        return (
            <Well title="General" mode="ghost" hideFooter={true}>
                <FormLayoutRow
                    label="Status"
                    loading={loading}
                    skeleton={{ width: 100 }}
                    value={<StatusBadge type="generic" status={enabled ? 'ENABLED' : 'DISABLED'} expanded={true} />}
                />

                <FormLayoutRow
                    label="Source URL"
                    loading={loading}
                    skeleton={{ width: 500 }}
                    value={
                        <a href={url} target="_blank" rel="noreferrer noopener">
                            <span>{url}</span>
                        </a>
                    }
                />
            </Well>
        )
    }

    function buildScheduleDetailsWell(feed: FeedDto) {
        const { config } = feed

        return (
            <Well title="Scheduling" mode="ghost" hideFooter={true}>
                <FormLayoutRow
                    label="Send Dates"
                    loading={loading}
                    skeleton={{ width: 200 }}
                    value={
                        <>
                            {moment(config?.schedule.limited_schedule_dates.start_date).format('ddd, LL')}
                            <span> through </span>
                            {config?.schedule.limited_schedule_dates.end_date
                                ? moment(config?.schedule.limited_schedule_dates.end_date).format('ddd, LL')
                                : 'Ongoing'}
                        </>
                    }
                />

                <FormLayoutRow
                    label="Send Times"
                    loading={loading}
                    skeleton={{ width: 300 }}
                    value={
                        config?.schedule.start_time ? (
                            <>
                                {convertToStringDay(config.schedule.day_of_week_iso)}
                                <span> at </span>
                                {moment(config.schedule.start_time, 'HH:mm').format('h:mm A')}
                                <span> in </span>
                                {config.schedule.send_strategy !== FeedSendStrategy.SUBSCRIBER_TIME_ZONE
                                    ? "Domain's Time Zone"
                                    : "Subscriber's Time Zone"}
                            </>
                        ) : (
                            'New Article Posted'
                        )
                    }
                />

                <FormLayoutRow
                    label="Pre-Schedule Delay"
                    loading={loading}
                    skeleton={{ width: 50 }}
                    value={
                        (config?.schedule as IFixedFeedSchedule)?.delivery_delay
                            ? (config.schedule as IFixedFeedSchedule).delivery_delay.display_metric ===
                              RelativeDateDisplayMetric.MINUTES
                                ? secondsToMinutes(
                                      (config.schedule as IFixedFeedSchedule).delivery_delay.interval_seconds,
                                  ) +
                                  ` ${(config.schedule as IFixedFeedSchedule).delivery_delay.display_metric}` +
                                  ' before send time'
                                : secondsToHours(
                                      (config.schedule as IFixedFeedSchedule).delivery_delay.interval_seconds,
                                  ) +
                                  ` ${(config.schedule as IFixedFeedSchedule).delivery_delay.display_metric}` +
                                  ' before send time'
                            : null
                    }
                />
            </Well>
        )
    }

    function buildTargetingDetailsWell(feed: FeedDto) {
        const { domain_id } = feed

        const buildSegmentsList = (segments: SegmentDto[] | undefined, defaultValue?: string) => {
            if (!segments?.length) {
                return defaultValue ? <Tag>{defaultValue}</Tag> : null
            }

            return segments.map(({ id, name }: SegmentDto) => (
                <a
                    key={`i-${id}`}
                    href={`${aqe.defaults.publicPlatformDomain}/domains/${domain_id}/segments/${id}/summary`}
                    target="_blank"
                >
                    <Tag key={`segment-${id}`}>{name}</Tag>
                </a>
            ))
        }

        return (
            <Well title="Targeting" mode="ghost" hideFooter={true}>
                <FormLayoutRow
                    label="Targeted Audience(s)"
                    loading={loading}
                    skeleton={{ width: 150 }}
                    value={buildSegmentsList(feed.segments, 'All Push Subscribers')}
                />

                <FormLayoutRow
                    label="Excluded Audience(s)"
                    loading={loading}
                    skeleton={{ width: 150 }}
                    value={buildSegmentsList(feed.excluded_segments)}
                />
            </Well>
        )
    }

    function buildRestrictionsWell(feed: FeedDto) {
        const { config } = feed

        interface IStringListProps {
            label: string
            values: string[] | undefined
            maxDisplayLength?: number
        }
        const StringList = ({ label, values, maxDisplayLength }: IStringListProps) => {
            if (!values?.length) {
                return null
            }

            const visibleValues = maxDisplayLength ? values.slice(0, maxDisplayLength) : values
            const addtValues = maxDisplayLength ? values.slice(maxDisplayLength + 1) : []

            return (
                <FormLayoutRow
                    label={label}
                    value={
                        <>
                            {visibleValues.map((value, idx) => (
                                <Tag key={idx}>{value}</Tag>
                            ))}
                            {!!addtValues.length && (
                                <Popover
                                    trigger={['hover']}
                                    title={`Additional ${label}`}
                                    content={addtValues.map((value, idx) => (
                                        <div key={idx}>{value}</div>
                                    ))}
                                >
                                    <Tag>+{addtValues.length} more</Tag>
                                </Popover>
                            )}
                        </>
                    }
                />
            )
        }

        const hasNonUrlRestrictions =
            !!config?.required_keywords.length ||
            !!config?.restricted_keywords.length ||
            !!config?.title_denylist.length
        const hasUrlRestrictions = !!config?.url_allowlist.length || !!config?.url_denylist.length
        const hasRestrictions = hasNonUrlRestrictions || hasUrlRestrictions

        const maxVisibleKeywords = 3
        const maxVisibleUrls = 5

        return (
            <Well hideHeader={true} hideFooter={true} className="type-2" mode="ghost">
                {!hasRestrictions ? (
                    <Well title="Restrictions" mode="ghost" hideFooter={true}>
                        {loading ? <SkeletonBlock width={700} /> : 'No restrictions'}
                    </Well>
                ) : (
                    <>
                        {hasNonUrlRestrictions && (
                            <Row wrap={false}>
                                <Col flex={1} style={{ flexBasis: '50%', flexGrow: 0, flexShrink: 0 }}>
                                    <Well title="Restrictions" mode="ghost" hideFooter={true}>
                                        <StringList
                                            label="Required Keyword(s)"
                                            values={config?.required_keywords}
                                            maxDisplayLength={maxVisibleKeywords}
                                        />
                                        <StringList
                                            label="Restricted Keyword(s)"
                                            values={config?.restricted_keywords}
                                            maxDisplayLength={maxVisibleKeywords}
                                        />
                                    </Well>
                                </Col>
                                <Col flex={1} style={{ flexBasis: '50%', flexGrow: 0, flexShrink: 0 }}>
                                    <Well title={<div style={{ height: '22px' }}></div>} mode="ghost" hideFooter={true}>
                                        <StringList
                                            label="Denied Title(s)"
                                            values={config?.title_denylist}
                                            maxDisplayLength={maxVisibleKeywords}
                                        />
                                    </Well>
                                </Col>
                            </Row>
                        )}
                        {hasNonUrlRestrictions && hasUrlRestrictions && <Divider />}
                        {hasUrlRestrictions && (
                            <Row>
                                <Col>
                                    <Well mode="ghost" hideHeader={true} hideFooter={true}>
                                        <StringList
                                            label="Allowed URL(s)"
                                            values={config?.url_allowlist}
                                            maxDisplayLength={maxVisibleUrls}
                                        />
                                        <StringList
                                            label="Denied URL(s)"
                                            values={config?.url_denylist}
                                            maxDisplayLength={maxVisibleUrls}
                                        />
                                    </Well>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </Well>
        )
    }

    function buildDetails(): React.ReactNode {
        return (
            <>
                <Well className={classNames('type-3', 'nested')} title="Details" hideFooter={true}>
                    {buildGeneralDetailsWell(data.feed)}
                    <Well hideHeader={true} hideFooter={true} className="type-2" mode="ghost">
                        <Row wrap={false}>
                            <Col flex={1} style={{ flexBasis: '50%', flexGrow: 0, flexShrink: 0 }}>
                                {buildScheduleDetailsWell(data.feed)}
                            </Col>
                            <Col flex={1} style={{ flexBasis: '50%', flexGrow: 0, flexShrink: 0 }}>
                                {buildTargetingDetailsWell(data.feed)}
                            </Col>
                        </Row>
                    </Well>
                    {buildRestrictionsWell(data.feed)}
                </Well>
            </>
        )
    }

    function render() {
        const { feed } = data

        return (
            <div className="feed-details">
                <div className="feed-title">
                    <PageHeader
                        append={
                            feed && (
                                <Breadcrumb>
                                    <Breadcrumb.Item href={DOMAINS_ROUTE} onClick={handleBreadcrumbClick}>
                                        Domains
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item
                                        href={`${DOMAINS_ROUTE}/${feed.domain_id}`}
                                        onClick={handleBreadcrumbClick}
                                    >
                                        {props.domainName}
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item
                                        href={`${DOMAINS_ROUTE}/${feed.domain_id}/feeds`}
                                        onClick={handleBreadcrumbClick}
                                    >
                                        Feeds
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item>ID: {feed.id}</Breadcrumb.Item>
                                </Breadcrumb>
                            )
                        }
                        onTitleSet={noop}
                        title={feed && (feed.name || 'Feed Configuration')}
                        loading={loading}
                    />
                    {feed && <div className="append">{buildDetails()}</div>}
                </div>
            </div>
        )
    }

    return render()
}
